import React, { useState, useCallback, CSSProperties } from "react";
import { Link, styled, Tooltip } from "@mui/material";
import { RagGuideline } from "fe-common/graphql/types";
import { ReactComponent as GuidelineIcon } from "../assets/guidelineIcon.svg";
import { ReactComponent as OpenInNewIcon } from "../assets/openInNewIcon.svg";
import { ReactComponent as KnowledgeIcon } from "../assets/knowlengeIcon.svg";
import {
  TranslateByGender,
  useTranslateByGender,
} from "fe-common/locale/useTranslateByGender";
import { NO_GUIDELINE } from "./types";

//region [[ Styles ]]

const GuidelineIndicatorView = styled("div")({
  fontSize: 13,
  lineHeight: 1.5,
  margin: 0,
  padding: 0,
  marginBottom: 14,
  display: "flex",
  alignItems: "stretch",
  gap: 10,
  // marginBlockStart: -5,
  // marginBlockEnd: 10,
  // paddingInlineStart: 5,
  // paddingInlineEnd: 16,
  // width: "fit-content",
});
const GuidelineButtonStyled = styled("div")<{
  active: boolean;
  disabled?: boolean;
  single?: boolean;
  customStyle?: CSSProperties;
}>(({ theme, active, disabled, single, customStyle }) => ({
  border: `${!single && active ? "2px" : "1px"} solid ${theme.palette.divider}`,
  padding: active ? 5 : 6,
  fontSize: 12,
  lineHeight: 1.3,
  borderRadius: 8,
  textAlign: "start",
  width: single ? "auto" : 172,
  height: single ? "auto" : 106,
  cursor: active || disabled ? "auto" : "pointer",
  background: active ? theme.palette.primary.light : "transparent",
  overflow: "hidden",
  "&:hover": disabled ? {} : { background: theme.palette.primary.light },
  "&.no-guidelines": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  "& .guideline-link": {
    marginLeft: "auto",
  },
  "& .guideline-link svg": {
    height: 11,
    marginBottom: -1,
  },
  "& .guideline-link:hover": {
    color: theme.palette.primary.light,
  },
  "&:dir(rtl) .guideline-link": {
    marginLeft: "none",
    marginRight: "auto",
  },
  ...customStyle,
}));
const NoGuidelines = styled("div")({
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  fontWeight: 600,
  gap: 4,
  marginTop: -5,
  "& span": {
    width: 104,
  },
  "& svg": {
    width: 25,
    height: 25,
  },
});
const GuidelineTop = styled("div")({
  display: "inline-flex",
  fontSize: 11,
  gap: 6,
  alignItems: "center",
  marginBottom: 6,
  lineHeight: "24px",
  borderRadius: 7.2,
  justifyContent: "start",
  width: "auto",
  fontWeight: 600,
});

const GuidelineTitle = styled("div")({
  maxHeight: 64,
  overflow: "hidden",
  textOverflow: "ellipsis",
  position: "relative",
});

const GuidelineSource = styled("div")({
  color: "#00000088",
});

//endregion [[ Styles ]]

//region [[ Props ]]
export type indicatorGuideline = RagGuideline & { disabled?: boolean };

export interface GuidelineIndicatorProps {
  guidelines: indicatorGuideline[];
  onChange?: (fromId: string, toId: string) => void;
  disabled?: boolean;
  showIgnoreButton?: boolean;
  buttonStyle?: CSSProperties;
}

export interface GuideLineButtonProps {
  guideline: indicatorGuideline;
  onClick: () => void;
  active: boolean;
  translate: TranslateByGender;
  index: number;
  totalGuidelines?: number;
  single?: boolean;
  buttonStyle?: CSSProperties;
  disabled?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const GuidelineIndicator = ({
  guidelines,
  onChange,
  disabled,
  showIgnoreButton,
  buttonStyle,
}: GuidelineIndicatorProps) => {
  const [activeGuidelineId, setActiveGuidelineId] = useState(
    guidelines?.[0].id,
  );
  const { translate } = useTranslateByGender();

  const handleGuidelineSelect = useCallback(
    (fromId: string, toId: string) => {
      if (disabled) return;
      setActiveGuidelineId(toId);
      if (onChange) onChange(fromId, toId);
    },
    [onChange, disabled],
  );

  return (
    <GuidelineIndicatorView className="guidelines">
      {guidelines.map((guideline, index) => (
        <GuidelineButton
          key={"guideline" + index}
          guideline={guideline}
          index={index}
          onClick={() => handleGuidelineSelect(activeGuidelineId, guideline.id)}
          active={activeGuidelineId === guideline.id}
          disabled={disabled || guideline.disabled}
          translate={translate}
          totalGuidelines={guidelines.length}
          single={guidelines.length === 1 && !showIgnoreButton}
          buttonStyle={buttonStyle}
        />
      ))}
      {showIgnoreButton && (
        <GuidelineButtonStyled
          key={"no-guideline"}
          className={"guideline-button no-guidelines"}
          onClick={() => {
            if (!disabled)
              handleGuidelineSelect(activeGuidelineId, NO_GUIDELINE);
          }}
          active={activeGuidelineId === NO_GUIDELINE}
          disabled={disabled}
          customStyle={buttonStyle}
        >
          <NoGuidelines>
            <KnowledgeIcon />
            <span>{translate("CLINICAL_GUIDELINE_IGNORE")}</span>
          </NoGuidelines>
        </GuidelineButtonStyled>
      )}
    </GuidelineIndicatorView>
  );
};

const GuidelineButton = ({
  guideline,
  onClick,
  active,
  translate,
  index,
  totalGuidelines,
  buttonStyle,
  disabled,
  single,
}: GuideLineButtonProps) => {
  const maxChars = 87;
  const title = `‘${guideline.title}’`;
  const source: string = guideline.properties?.length
    ? guideline.properties.map((p) => p.value).join(" - ") +
      (guideline["source"] ? " | " + guideline["source"] : "")
    : guideline["source"] || "";
  let titleStr = title;
  let sourceStr = source;
  let showTooltip = false;
  const titleLength = title.length + (guideline.url ? 3 : 0);
  if (titleLength > maxChars) {
    titleStr = titleStr.slice(0, maxChars) + "...";
    showTooltip = true;
    sourceStr = "";
  } else {
    if (source.length + titleLength > maxChars) {
      sourceStr = sourceStr.slice(0, maxChars - titleLength) + "...";
      showTooltip = true;
    }
  }

  const button = (
    <GuidelineButtonStyled
      key={"guideline" + index}
      className={"guideline-button"}
      onClick={() => {
        if (!disabled && !active) onClick();
      }}
      active={active}
      disabled={disabled}
      single={single}
      customStyle={buttonStyle}
    >
      <GuidelineTop>
        <GuidelineIcon />
        {translate("CLINICAL_GUIDELINE")} {totalGuidelines > 1 ? index + 1 : ""}
        {guideline.url && (
          <Link
            href={guideline.url}
            target="_blank"
            className={"guideline-link"}
          >
            <OpenInNewIcon />
          </Link>
        )}
      </GuidelineTop>
      <GuidelineTitle>
        {titleStr}
        <GuidelineSource className="guideline-src">{sourceStr}</GuidelineSource>
      </GuidelineTitle>
    </GuidelineButtonStyled>
  );

  return showTooltip ? (
    <Tooltip
      title={title + "\n" + source}
      arrow
      componentsProps={{ popper: { style: { textAlign: "center" } } }}
    >
      {button}
    </Tooltip>
  ) : (
    button
  );
};
